import { Component, TemplateFunction } from '@ribajs/core'
import type { SibNewsletterComponent } from './sib-newsletter.component.js'

/**
 * The type for the scope
 */
interface Scope {
  // Properties

  email: string
  givenname: string
  familyname: string
  /** True if the customer has enabled the newsletter checkbox */
  newsletter?: boolean

  // Methods

  submit: SbRegisterComponent['submit']
  onNativeSubmit: SbRegisterComponent['onNativeSubmit']

  // Elements
  registerFormEl?: HTMLFormElement
  newsletterEl?: SibNewsletterComponent
}

/**
 * A register / create new account component
 */
export class SbRegisterComponent extends Component {
  /**
   * The tag name of the element, in this case `<sb-register>...</sb-register>`
   */
  public static tagName = 'sb-register'

  public scope: Scope = {
    email: '',
    givenname: '',
    familyname: '',
    newsletter: false,

    // Methods

    submit: this.submit,
    onNativeSubmit: this.onNativeSubmit,
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return ['success-message-template', 'error-message-template']
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbRegisterComponent.observedAttributes)
  }

  async afterBind() {
    await super.afterBind()
    this.scope.registerFormEl ??=
      this.querySelector('form#create_customer') ||
      this.closest('form#create_customer') ||
      undefined
    this.setReturnToInputByUrlSearchParam()
    this.classList.add(`${SbRegisterComponent.tagName}-ready`)
  }

  public async onNativeSubmit(e: Event) {
    e.preventDefault()
    await this.submit()
  }

  public async submit() {
    if (!this.scope.registerFormEl) {
      throw new Error('registerFormEl not found')
    }

    console.log('submit', this.scope)

    // Check form validity first
    if (!this.scope.registerFormEl.checkValidity()) {
      this.scope.registerFormEl.reportValidity()
      return
    }

    // Submit newsletter if enabled
    if (this.scope.newsletter) {
      await this.scope.newsletterEl?.submit()
    }

    this.scope.registerFormEl.submit()
  }

  /**
   * Set's the return to input value by the url search param `return_to`.
   * This way the user can be redirected to the page he came from.
   * @returns
   */
  setReturnToInputByUrlSearchParam() {
    const params = new URLSearchParams(window.location.search)

    const returnUrl = params.get('return_to')
    if (!returnUrl) {
      return
    }
    const inputReturnTo = this.getElementsByClassName(
      'return_to',
    ) as HTMLCollectionOf<HTMLInputElement>
    for (const input in inputReturnTo) {
      const inputEl = inputReturnTo[input]
      if (inputEl) {
        inputEl.value = returnUrl
      }
    }
  }

  /**
   * Returns the template, if null is returned, the child html of this element / component is not replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
